import axios from 'axios';
import { API_BASE_URL } from '../config';
import { setAdminSessionNotice, resolveAdminSessionEndReason } from '../utils/adminSessionNotice';

export const BASE_URL = API_BASE_URL;

const client = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getMemberToken = () => {
  try {
    const localRaw = localStorage.getItem('alu_user_auth');
    if (localRaw) {
      const localParsed = JSON.parse(localRaw);
      if (localParsed?.token) {
        return String(localParsed.token);
      }
    }

    const sessionRaw = sessionStorage.getItem('alu_user_auth_session');
    if (sessionRaw) {
      const sessionParsed = JSON.parse(sessionRaw);
      if (sessionParsed?.token) {
        return String(sessionParsed.token);
      }
    }
  } catch {
    // Ignore malformed auth payloads and continue fallback checks.
  }

  const legacyToken = localStorage.getItem('alu_token');
  if (legacyToken) {
    return String(legacyToken);
  }

  return null;
};

const getAdminToken = () => localStorage.getItem('adminToken');

const initialToken = getAdminToken() || getMemberToken();
if (initialToken) {
  client.defaults.headers.common.Authorization = `Bearer ${initialToken}`;
}

let hasHandledAdminSessionEnd = false;
const memberSessionExpiredListeners = new Set();

const notifyMemberSessionExpired = (payload = {}) => {
  memberSessionExpiredListeners.forEach((listener) => {
    try {
      listener(payload);
    } catch {
      // Never let listener failures break API error flow.
    }
  });
};

export const onMemberSessionExpired = (handler) => {
  if (typeof handler !== 'function') {
    return () => {};
  }

  memberSessionExpiredListeners.add(handler);
  return () => {
    memberSessionExpiredListeners.delete(handler);
  };
};

const isAdminApiRequest = (requestUrl = '') => /\/api\/admin\//i.test(String(requestUrl));

const isAdminAuthRequest = (requestUrl = '') => /\/api\/admin\/auth\//i.test(String(requestUrl));

const isMemberAuthRequest = (requestUrl = '') =>
  /\/api\/auth\/(login|login-verify|send-otp|verify-otp|signup|forgot-password|reset-password|google)/i
    .test(String(requestUrl));

const getBackendErrorMessage = (error) => {
  const message = error?.response?.data?.message;
  if (typeof message === 'string' && message.trim()) {
    return message.trim();
  }

  const fallbackError = error?.response?.data?.error;
  if (typeof fallbackError === 'string' && fallbackError.trim()) {
    return fallbackError.trim();
  }

  return '';
};

const isMemberSessionExpiredMessage = (message) => {
  const normalized = String(message ?? '').trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  return (
    normalized.includes('session expired')
    || normalized.includes('invalid or expired member token')
    || normalized.includes('member authentication required')
    || normalized.includes('authentication required')
    || normalized.includes('invalid member token')
  );
};

client.interceptors.request.use((config) => {
  const nextConfig = { ...config };
  nextConfig.headers = nextConfig.headers ?? {};

  const requestUrl = String(nextConfig.url ?? '');
  const preferredToken = isAdminApiRequest(requestUrl)
    ? (getAdminToken() || getMemberToken())
    : (getMemberToken() || getAdminToken());

  if (preferredToken) {
    nextConfig.headers.Authorization = `Bearer ${preferredToken}`;
  } else if (nextConfig.headers.Authorization) {
    delete nextConfig.headers.Authorization;
  }

  return nextConfig;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = Number(error?.response?.status);
    const requestUrl = String(error?.config?.url ?? '');
    const isSessionError = status === 401;
    const backendMessage = getBackendErrorMessage(error);

    const hasAdminSession = Boolean(localStorage.getItem('adminToken'));
    const hasMemberSession = Boolean(getMemberToken());
    const onAdminLoginRoute = typeof window !== 'undefined' && /^\/admin\/(login|forgot-password|reset-password)/i.test(window.location?.pathname || '');
    const onMemberAuthRoute = typeof window !== 'undefined' && /^\/(login|register|forgot-password|reset-password|oauth\/google)/i.test(window.location?.pathname || '');

    if (
      !hasHandledAdminSessionEnd
      && isSessionError
      && hasAdminSession
      && isAdminApiRequest(requestUrl)
      && !isAdminAuthRequest(requestUrl)
      && !onAdminLoginRoute
    ) {
      hasHandledAdminSessionEnd = true;
      const backendMessage = String(error?.response?.data?.message ?? '');
      const reason = resolveAdminSessionEndReason(backendMessage);
      setAdminSessionNotice({ reason, backendMessage });

      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      delete client.defaults.headers.common.Authorization;

      if (typeof window !== 'undefined') {
        window.location.assign('/admin/login');
      }
    }

    if (
      isSessionError
      && !isAdminApiRequest(requestUrl)
      && !isMemberAuthRequest(requestUrl)
      && hasMemberSession
      && !onMemberAuthRoute
      && isMemberSessionExpiredMessage(backendMessage)
    ) {
      notifyMemberSessionExpired({
        status,
        requestUrl,
        backendMessage,
      });
    }

    return Promise.reject(error);
  },
);

export default client;
